// ============================================================================
// EL CATÁLOGO DE TU BOT — T-CAT-13, cubre **R-CAT-01**.
//
// ⛔⛔ LA TRAMPA DEL TRAMO, Y ES TODO EL TRAMO: CONTAR EL CATÁLOGO DEL ERP NO ES
// CONTAR EL DEL BOT. La pantalla fácil es un `count(*)` sobre `erp.v_catalogo_bot`
// y pintar «90 productos». Sería una pantalla que mide lo que no es:
//   1 · si el worker lleva horas sin poder refrescar —el ERP caído, un permiso
//       que se cayó, el proceso muerto—, el bot atiende con lo último bueno **o
//       con nada**, y la pantalla seguiría diciendo «90 productos» tan contenta;
//   2 · abriría una SEGUNDA PUERTA al catálogo. La casa decidió que la puerta es
//       `erp.catalogo_bot(uuid)`; y esa vista NO es `security_invoker`, así que
//       leerla se salta la RLS de las tablas base. Filtrar por el `tenant_id` del
//       panel tampoco arregla nada: el de la vista es de OTRO mundo de
//       identificadores y daría CERO productos con el bot vendiendo con noventa.
// ⇒ Lo que se enseña es el ESTADO DEL BOT (`chatai.estado_catalogo`, migr 0054),
//   escrito por el único que lo sabe: el worker. **Y SU EDAD.** V-150.
//
// ⭐ LA MITAD QUE EVITA LA PANTALLA QUE MIENTE ES EL AVISO DE VIEJO (V-152). Un
// número sin fecha tranquiliza siempre; un número con diez minutos encima dice
// que el bot está atendiendo con datos rancios. Si algún día hay que elegir qué
// se cae de esta pantalla, se cae el número antes que el aviso.
//
// ⛔⛔ NI UN CAMPO EDITABLE, PARA NINGÚN ROL (V-154). El catálogo se edita en el
// ERP y en ningún otro sitio: dos sitios donde tocar un precio es el bot
// cotizando distinto a la factura. Ni `input`, ni `textarea`, ni `select`, ni un
// botón que escriba. La base sostiene lo mismo por su lado — `authenticated` no
// tiene `insert`/`update`/`delete` sobre `estado_catalogo`.
//
// Molde: `Guion.tsx` (T-GUI-10), que también es de solo mirar.
// ============================================================================
import { useEffect, useState } from "react";
import { Boxes, ExternalLink } from "lucide-react";
import { Aviso, SkeletonLista } from "./piezas.tsx";
import { desdeHace } from "./FranjaAlertas.tsx";
import { explicarError, leerEstadoCatalogo } from "./datos.ts";
import {
  MINUTOS_CATALOGO_VIEJO, MINUTOS_REFRESCO_CATALOGO, MS_RELOJ_CATALOGO, catalogoViejo,
} from "./tipos.ts";
import type { ContextoPanel, EstadoCatalogo } from "./tipos.ts";

/**
 * Igual que en `Guion.tsx`: `explicarError` traduce el `42501` como «No tienes
 * acceso a esta conversación», que aquí sería un diagnóstico FALSO y mandaría a
 * la persona a buscar el problema donde no está. Un mensaje amable pero
 * equivocado no es mejor que el crudo de Postgres — y el crudo NO sale a
 * pantalla nunca (V-153).
 */
function explicarFalloDeCatalogo(e: any): string {
  const code = e?.code ?? "";
  const msg = typeof e?.message === "string" ? e.message : "";
  if (code === "42501" || /row-level security|permission denied/i.test(msg)) {
    return "Tu cuenta no puede ver el catálogo de este negocio. Pídeselo a tu contacto de Victorix.";
  }
  const traducido = explicarError(e);
  return traducido === msg
    ? "No se pudo leer el estado del catálogo. Vuelve a entrar en un momento."
    : traducido;
}

export function Catalogo({ ctx, urlCatalogoErp }: {
  ctx: ContextoPanel;
  /**
   * A dónde lleva «Abrir el Catálogo del ERP». La librería NO enruta ni conoce
   * dominios (C7-D1): lo pone el anfitrión. ⛔ Si no llega, la frase se pinta
   * SIN enlace en vez de inventar una URL: un enlace que lleva a un 404 es peor
   * que decir dónde se administra el catálogo y ya.
   */
  urlCatalogoErp?: string;
}) {
  const { sb, tenantId } = ctx;

  const [estado, setEstado] = useState<EstadoCatalogo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const e = await leerEstadoCatalogo(sb, tenantId);
        if (!vivo) return;
        setEstado(e);
        setError(null);
      } catch (e) {
        if (!vivo) return;
        setEstado(null);
        setError(explicarFalloDeCatalogo(e));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId]);

  /**
   * ⭐⭐ EL RELOJ DE LA PANTALLA (V-156, hallazgo H1 del fiscal).
   *
   * ⛔ SIN ESTO LA PANTALLA ES UNA FOTO DEL MOMENTO EN QUE SE ABRIÓ. La edad del
   * dato se calculaba una sola vez: con `/catalogo` dejada abierta y el worker
   * muriéndose cinco minutos después, seguía diciendo «refrescado hace 3
   * minutos» para siempre y el aviso de viejo no salía nunca. Un panel de
   * trabajo se deja abierto todo el día — ése es el caso normal, no el raro.
   *
   * ⚠️ SON DOS DISPARADORES Y HACEN FALTA LOS DOS. El intervalo cubre la
   * pestaña a la vista; `visibilitychange`/`focus` cubren lo que el intervalo
   * NO puede: un portátil dormido, o una pestaña de fondo cuyos temporizadores
   * el navegador estrangula. Al volver, la edad se recalcula YA.
   * ⚠️ No se vuelve a consultar la base: esto solo recalcula texto que ya está
   * descargado. Cero red.
   */
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const marcar = () => setAhora(new Date());
    const t = setInterval(marcar, MS_RELOJ_CATALOGO);
    document.addEventListener("visibilitychange", marcar);
    window.addEventListener("focus", marcar);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", marcar);
      window.removeEventListener("focus", marcar);
    };
  }, []);

  const viejo = estado ? catalogoViejo(estado.generado_en, ahora) : false;

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">El catálogo de tu bot</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">
          Qué productos tiene cargados ahora mismo, de dónde salen y cuándo los leyó.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-3">
          {error && <Aviso tono="peligro">{error}</Aviso>}

          {/* R-CAT-01 · se dice dónde se edita ANTES de que alguien lo busque aquí. */}
          <Aviso tono="alerta">
            El catálogo se administra en el <strong>Catálogo del ERP</strong> y no se edita desde
            aquí. Es lo que impide que el bot cotice distinto a la factura: un precio, un sitio.
          </Aviso>

          {cargando && <SkeletonLista filas={2} />}

          {!cargando && !error && !estado && (
            <div data-sin-catalogo className="border border-borde rounded-xl bg-superficie p-5 text-center">
              <p className="text-[14px] font-semibold">Este negocio todavía no tiene catálogo conectado</p>
              <p className="text-[12.5px] text-tinta-2 mt-1.5 leading-snug">
                Cuando su catálogo del ERP quede enlazado, el bot lo relee solo cada{" "}
                {MINUTOS_REFRESCO_CATALOGO} minutos y aquí aparecerá cuántos productos tiene y
                cuándo los leyó. Mientras tanto, el bot no cotiza: prefiere callarse antes que
                inventar un precio.
              </p>
            </div>
          )}

          {!cargando && estado && (<>
            {/* ⭐ EL AVISO VA ARRIBA DEL NÚMERO, a propósito: quien entra deprisa
                lee lo primero. Si el dato está rancio, eso es lo primero. */}
            {viejo && (
              <Aviso tono="peligro">
                <span data-catalogo-viejo>
                  <strong>Este dato está viejo:</strong> el bot leyó el catálogo{" "}
                  {desdeHace(estado.generado_en, ahora)} y debería releerlo cada{" "}
                  {MINUTOS_REFRESCO_CATALOGO} minutos. Ahora mismo está atendiendo con lo último
                  bueno que alcanzó a cargar — o sin catálogo. No des el número de abajo por bueno
                  y avisa a Victorix.
                </span>
              </Aviso>
            )}

            <section data-estado-catalogo data-viejo={viejo ? "si" : "no"}
              className="border border-borde rounded-xl bg-superficie p-5">
              <h2 className="text-[14px] font-semibold flex items-center gap-2">
                <span className="text-tinta-2"><Boxes size={15} /></span>
                Lo que tu bot tiene cargado
              </h2>
              <p data-conteo className="text-[15px] mt-2.5 leading-snug">
                <strong>{estado.productos} productos del ERP</strong>
                {" · refrescado "}{desdeHace(estado.generado_en, ahora)}
              </p>
              <p data-variantes className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                {estado.variantes} variantes (tallas, colores y medidas): es por variante como el
                bot cotiza.
              </p>
              <p data-fuente className="text-[12px] text-tinta-2 mt-2 leading-snug">
                Origen: <span className="font-mono">{estado.fuente}</span>. Este número lo escribe
                el bot cada vez que consigue releer el catálogo, así que dice lo que el bot tiene
                en la mano — no lo que el ERP tiene guardado.
              </p>
            </section>

            <p className="text-[12.5px] text-tinta-2 leading-snug px-1">
              {urlCatalogoErp
                ? <a data-enlace-erp href={urlCatalogoErp} target="_blank" rel="noreferrer"
                    className="text-marca hover:underline inline-flex items-center gap-1">
                    Abrir el Catálogo del ERP <ExternalLink size={13} />
                  </a>
                : <span data-sin-enlace-erp>
                    Los productos, precios y fotos se cambian en el Catálogo del ERP.
                  </span>}
              {" "}Lo que cambies allí lo toma el bot solo, en menos de{" "}
              {MINUTOS_REFRESCO_CATALOGO} minutos. Si pasan más de {MINUTOS_CATALOGO_VIEJO},
              esta pantalla te lo dice.
            </p>
          </>)}
        </div>
      </div>
    </div>
  );
}
